/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import React, { memo } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';

import { Typography } from '@mui/material';

import { UserApiService } from '../../../../services/UserApiService';
import { RootState } from '../../../../store';
import { userLogout } from '../../../../store/auth/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { UnprocessableEntityPayload } from '../../../../types/api/UnprocessableEntityPayload';
import { UpdateUserPasswordRequestBody } from '../../../../types/user/UpdateUserPasswordRequestBody';
import { unprocessibleEntityToObject } from '../../../../utils/unprocessibleEntityToObject';
import { useObjectState } from '../../../../utils/useObjectState';
import { validationSchema } from './ChangePassword.consts';
import { ChangePasswordForm } from './ChangePassword.Form';
import { useChangePasswordStyles } from './ChangePassword.styles';
import { ChangePasswordFormValues, ResetApiErrorForField, SignUpPageApiValidationErrors } from './ChangePassword.types';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type State = {
  isFetching: boolean;
  errorMessage?: string;
  apiErrors: SignUpPageApiValidationErrors;
  values: ChangePasswordFormValues;
};

const ChangePasswordComponent = memo<PropsFromRedux>(props => {
  const {
    auth: { user },
  } = props;
  const classes = useChangePasswordStyles();
  const dispatch = useDispatch();
  const [state, setState] = useObjectState<State>({
    isFetching: false,
    errorMessage: undefined,
    apiErrors: {},
    values: {
      oldPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
  });
  const { apiErrors, isFetching, errorMessage, values } = state;

  const submit = async (data: ChangePasswordFormValues) => {
    if (!user) {
      return;
    }
    const updateUserPasswordData: UpdateUserPasswordRequestBody = {
      oldPassword: data.oldPassword.trim(),
      newPassword: data.newPassword.trim(),
    };
    try {
      setState({ isFetching: true });
      await UserApiService.updateUser(updateUserPasswordData);
      dispatch(userLogout());
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      if (error.response?.data.message === 'unprocessable_entity') {
        const payload: UnprocessableEntityPayload<keyof SignUpPageApiValidationErrors> = error.response.data.payload;
        setState({
          apiErrors: unprocessibleEntityToObject(payload),
          isFetching: false,
        });
      } else {
        setState({
          isFetching: false,
          errorMessage: error.response?.data.message,
        });
      }
    }
  };

  const resetApiError: ResetApiErrorForField = name => {
    if (apiErrors[name]) {
      setState({ apiErrors: { ...apiErrors, [name]: undefined } });
    }
  };

  return (
    <>
      <Typography variant="h4" className={classes.title}>
        Change or recover your password
      </Typography>
      <Formik<ChangePasswordFormValues> initialValues={values} validationSchema={validationSchema} onSubmit={submit}>
        {props => (
          <ChangePasswordForm
            {...props}
            apiErrors={apiErrors}
            resetApiError={resetApiError}
            errorMessage={errorMessage}
            isFetching={isFetching}
          />
        )}
      </Formik>
    </>
  );
});

export const ChangePassword = connector(ChangePasswordComponent);
